import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../axios';
import { useAuth } from '../context/AuthContext';
import Mt5Login from './Mt5Login';
import ConfigInput from './ConfigInput';
import BrandLogo from './BrandLogo';
import { fetchOnboardingStatus } from '../utils/onboarding';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { 
  FiRefreshCw,
  FiX,
  FiTrendingUp,
  FiTrendingDown,
  FiUser,
  FiPieChart,
  FiActivity,
  FiDollarSign as FiDollar,
} from 'react-icons/fi';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

// Format currency with color coding
const formatCurrency = (value, showSign = false, currency = 'USD') => {
  try {
    const numValue = parseFloat(value) || 0;
    const isNegative = numValue < 0;
    const absValue = Math.abs(numValue);
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(absValue);
    
    const sign = showSign ? (isNegative ? '-' : '+') : '';
    const colorClass = numValue === 0 ? 'text-gray-400' : numValue > 0 ? 'text-green-500' : 'text-red-500';
    
    return <span className={`${colorClass} font-medium`}>{sign}{formatted}</span>;
  } catch (error) {
    console.error('Error formatting currency:', error);
    return <span className="text-gray-400">$0.00</span>;
  }
};

// Format percentage with color coding
const formatPercentage = (value) => {
  const numValue = parseFloat(value) || 0;
  const colorClass = numValue === 0 ? 'text-gray-400' : numValue > 0 ? 'text-green-500' : 'text-red-500';
  const sign = numValue > 0 ? '+' : '';
  
  return <span className={`${colorClass} font-medium`}>{sign}{numValue.toFixed(2)}%</span>;
};

const normalizeTradeHistory = (trades) => {
  if (!Array.isArray(trades)) {
    return [];
  }

  return trades.map((trade) => ({
    ticket: trade.ticket || trade.id,
    symbol: trade.symbol,
    type: trade.type,
    volume: parseFloat(trade.volume || 0),
    openPrice: parseFloat(trade.openPrice ?? trade.entry_price ?? trade.price ?? 0),
    closePrice: parseFloat(trade.closePrice ?? trade.exit_price ?? trade.close_price ?? 0),
    profit: parseFloat(trade.profit || 0),
    openTime: trade.openTime || trade.entry_time || trade.time_open,
    closeTime: trade.closeTime || trade.exit_time || trade.time_close,
  }));
};

const buildPerformanceMetrics = (accountData, tradeStats) => {
  const dailyProfit = parseFloat(accountData?.profit || 0);
  const balance = parseFloat(accountData?.balance || 0);
  const baselineBalance = balance - dailyProfit;
  const dailyReturn = baselineBalance > 0 ? (dailyProfit / baselineBalance) * 100 : 0;

  return {
    dailyProfit,
    monthlyProfit: parseFloat(tradeStats?.total_profit || 0),
    dailyReturn,
    winRate: parseFloat(tradeStats?.win_rate || 0),
    totalTrades: parseInt(tradeStats?.total_trades || 0, 10),
  };
};

const buildBalanceHistory = (historyEntries, fallbackBalance) => {
  if (Array.isArray(historyEntries) && historyEntries.length > 0) {
    const orderedEntries = [...historyEntries].sort((left, right) => {
      const leftTime = new Date(left.timestamp).getTime();
      const rightTime = new Date(right.timestamp).getTime();
      return leftTime - rightTime;
    });
    return {
      labels: orderedEntries.map((entry) => {
        const date = new Date(entry.timestamp);
        return Number.isNaN(date.getTime())
          ? 'Unknown'
          : date.toLocaleDateString([], { month: 'short', day: 'numeric' });
      }),
      values: orderedEntries.map((entry) => parseFloat(entry.balance || 0)),
    };
  }

  return {
    labels: ['Now'],
    values: [parseFloat(fallbackBalance || 0)],
  };
};

// TradeRow component for rendering individual trade rows
const TradeRow = ({ trade, onCloseTrade }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const formatTime = (timestamp) => {
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) throw new Error('Invalid date');
      return {
        time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        date: date.toLocaleDateString()
      };
    } catch (e) {
      return { time: '--:--', date: '--/--/----' };
    }
  };

  const handleCloseTrade = async () => {
    if (!trade || !trade.ticket) return;

    try {
      setIsClosing(true);
      const response = await api.post('/api/trades/close', {
        ticket: trade.ticket,
        symbol: trade.symbol,
        volume: trade.volume
      });

      if (response.data.success) {
        toast.success(`Trade #${trade.ticket} closed successfully`);
        onCloseTrade && onCloseTrade(trade.ticket);
      } else {
        throw new Error(response.data.message || 'Failed to close trade');
      }
    } catch (error) {
      console.error('Error closing trade:', error);
      toast.error(`Failed to close trade: ${error.message}`);
    } finally {
      setIsClosing(false);
    }
  };

  const { time, date } = formatTime(trade?.time || new Date());
  const profit = parseFloat(trade?.profit || 0);
  const isPositive = profit > 0;
  const isNegative = profit < 0;
  
  const isBuy = trade?.type === 'buy' || trade?.type === 0; // Handle both string and number types
  const showProfitPercentage = profit !== 0;
  const tradeType = isBuy ? 'Buy' : 'Sell';
  const typeColor = isBuy ? 'text-green-500' : 'text-red-500';
  
  return (
    <tr 
      className={`transition-colors ${isHovered ? 'bg-white/[0.06]' : 'bg-transparent hover:bg-white/[0.03]'}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-slate-200">
        <div className="flex items-center">
          <div className={`mr-3 flex h-9 w-9 items-center justify-center rounded-2xl border ${isBuy ? 'border-emerald-300/20 bg-emerald-300/10' : 'border-rose-300/20 bg-rose-300/10'}`}>
            {isBuy ? <FiTrendingUp className="text-emerald-300" /> : <FiTrendingDown className="text-rose-300" />}
          </div>
          <div>
            <div className="font-medium">{trade?.symbol || '--'}</div>
            <div className="text-xs text-slate-500">{tradeType}</div>
          </div>
        </div>
      </td>
      <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-300">
        <div>{trade?.volume || '--'}</div>
        <div className="text-xs text-slate-500">Lots</div>
      </td>
      <td className="px-4 py-4 whitespace-nowrap text-sm">
        <div className={`font-medium ${typeColor}`}>{trade?.price?.toFixed(5) || '--'}</div>
        <div className="text-xs text-slate-500">Entry</div>
      </td>
      <td className="px-4 py-4 whitespace-nowrap text-sm">
        <div className="font-medium text-slate-300">{trade?.sl?.toFixed(5) || '--'}</div>
        <div className="text-xs text-slate-500">Stop Loss</div>
      </td>
      <td className="px-4 py-4 whitespace-nowrap text-sm">
        <div className="font-medium text-slate-300">{trade?.tp?.toFixed(5) || '--'}</div>
        <div className="text-xs text-slate-500">Take Profit</div>
      </td>
      <td className="px-4 py-4 whitespace-nowrap text-sm">
        <div className={isPositive ? 'text-emerald-300' : isNegative ? 'text-rose-300' : 'text-slate-500'}>
          {formatCurrency(profit, true)}
        </div>
        {showProfitPercentage && (
          <div className="text-xs text-slate-500">
            {isPositive ? '+' : ''}{(profit / (trade?.price * trade?.volume * 100) * 100).toFixed(2)}%
          </div>
        )}
      </td>
      <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-500">
        <div>{time}</div>
        <div className="text-xs">{date}</div>
      </td>
      <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
        <button
          onClick={handleCloseTrade}
          disabled={isClosing}
          className={`inline-flex items-center rounded-xl border px-3 py-2 text-xs font-semibold transition ${
            isClosing 
              ? 'border-white/10 bg-white/10 text-slate-400' 
              : 'border-rose-300/20 bg-rose-300/10 text-rose-200 hover:bg-rose-300/15 focus:outline-none'
          }`}
        >
          {isClosing ? 'Closing...' : 'Close'}
        </button>
      </td>
    </tr>
  );
};

// Main Dashboard Component
const Dashboard = () => {
  // Navigation and UI State
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { logout, subscriptionStatus } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isTrading, setIsTrading] = useState(false);
  const [isTradingActionPending, setIsTradingActionPending] = useState(false);
  
  // MT5 Connection State
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [mt5Connected, setMt5Connected] = useState(false);
  const [mt5Credentials, setMt5Credentials] = useState({
    login: '',
    password: '',
    server: '',
    broker_name: '',
    account_type: 'demo'
  });
  const [onboarding, setOnboarding] = useState({
    mt5_connected: false,
    config_set: false,
    onboarded: false,
  });
  
  // Trading Data State
  const [accountInfo, setAccountInfo] = useState(null);
  const [activePositions, setActivePositions] = useState([]);
  const [tradeHistory, setTradeHistory] = useState([]);
  const [performanceMetrics, setPerformanceMetrics] = useState({
    dailyProfit: 0,
    monthlyProfit: 0,
    winRate: 0,
    totalTrades: 0,
  });
  
  // Chart Data
  const [priceData, setPriceData] = useState({
    labels: [],
    datasets: [{
      label: 'Balance',
      data: [],
      borderColor: 'rgb(74, 222, 128)',
      backgroundColor: 'rgba(74, 222, 128, 0.1)',
      tension: 0.4,
      fill: true,
    }],
  });
  
  // Refs
  const dataRefreshInterval = useRef(null);
  const setupPromptHandled = useRef(false);
  const hasLinkedMt5Account = Boolean(accountInfo?.login || mt5Credentials.login || mt5Credentials.server);

  const refreshOnboarding = useCallback(async () => {
    const nextOnboarding = await fetchOnboardingStatus(api);
    setOnboarding(nextOnboarding);
    return nextOnboarding;
  }, []);

  const fetchTradingStatus = useCallback(async () => {
    try {
      const response = await api.get('/api/trading/status').catch(() => null);
      if (response?.data?.success) {
        setIsTrading(Boolean(response.data.is_running));
        setAccountInfo((previousAccountInfo) => (
          previousAccountInfo
            ? { ...previousAccountInfo, bot_running: Boolean(response.data.is_running), bot_start_time: response.data.bot_start_time || previousAccountInfo.bot_start_time }
            : previousAccountInfo
        ));
      }
    } catch (error) {
      console.debug('Error fetching trading status:', error.message);
    }
  }, []);
  
  // Fetch trading data for any linked account, even if MT5 is not marked as live-connected
  const fetchTradingData = useCallback(async () => {
    if (isRefreshing) {
      setIsLoading(false);
      return;
    }
    
    try {
      setIsRefreshing(true);
      
      // Fetch account info
      const accountResponse = await api.get('/api/account').catch(() => null);
      if (!accountResponse?.data) return;
      const nextAccountInfo = accountResponse.data;

      setAccountInfo(nextAccountInfo);
      setIsTrading(Boolean(nextAccountInfo.bot_running));
      
      // Only fetch other data if we have a valid account response with balance
      if (nextAccountInfo.balance !== undefined) {
        const [positionsResponse, historyResponse, tradeStatsResponse, accountHistoryResponse] = await Promise.all([
          api.get('/api/positions').catch(() => null),
          api.get('/api/history').catch(() => null),
          api.get('/api/trade_stats').catch(() => null),
          api.get('/api/account_history').catch(() => null),
        ]);

        setActivePositions(Array.isArray(positionsResponse?.data) ? positionsResponse.data : []);

        const normalizedTradeHistory = normalizeTradeHistory(historyResponse?.data);
        setTradeHistory(normalizedTradeHistory);

        const nextMetrics = buildPerformanceMetrics(nextAccountInfo, tradeStatsResponse?.data);
        setPerformanceMetrics(nextMetrics);

        const balanceHistory = buildBalanceHistory(accountHistoryResponse?.data, nextAccountInfo.balance);
        setPriceData((prev) => ({
          ...prev,
          labels: balanceHistory.labels,
          datasets: [{
            ...prev.datasets[0],
            data: balanceHistory.values,
          }],
        }));
      }
    } catch (error) {
      console.debug('Error in fetchTradingData:', error.message);
    } finally {
      setIsRefreshing(false);
      setIsLoading(false);
    }
  }, [isRefreshing]);
  
  // Initial load - just set loading to false since we don't need to load anything yet
  useEffect(() => {
    let mounted = true;

    const loadDashboardState = async () => {
      await refreshOnboarding();
      await fetchTradingStatus();
      await fetchAccountInfo();
      await fetchTradingData();
      if (mounted) {
        setIsLoading(false);
      }
    };

    loadDashboardState();

    return () => {
      mounted = false;
    };
  }, [refreshOnboarding, fetchTradingStatus, fetchTradingData]);
  
  // Handle MT5 connection status and account info loading
  useEffect(() => {
    let isMounted = true;
    
    const checkMt5Status = async () => {
      try {
        const response = await api.get('/api/mt5/status').catch(() => ({}));
        if (!isMounted) return;
        
        const wasConnected = mt5Connected;
        const isNowConnected = !!response?.data?.connected;
        const savedCredentials = response?.data?.credentials || null;
        
        setMt5Connected(isNowConnected);

        if (savedCredentials) {
          setMt5Credentials((previousCredentials) => ({
            ...previousCredentials,
            ...savedCredentials,
          }));
        }
        
        if (savedCredentials || isNowConnected || isTrading) {
          fetchTradingData();
        } else if (wasConnected) {
          // If we were connected but now we're not, reset data
          setAccountInfo(null);
          setActivePositions([]);
          setTradeHistory([]);
          setPerformanceMetrics({
            dailyProfit: 0,
            monthlyProfit: 0,
            winRate: 0,
            totalTrades: 0,
          });
          setPriceData((prev) => ({
            ...prev,
            labels: [],
            datasets: [{
              ...prev.datasets[0],
              data: [],
            }],
          }));
        }
      } catch (error) {
        console.debug('Error checking MT5 status:', error.message);
        if (isMounted) {
          setMt5Connected(false);
        }
      }
    };
    
    checkMt5Status();
    const statusInterval = setInterval(checkMt5Status, 30000);
    const tradingStatusInterval = setInterval(fetchTradingStatus, 10000);
    
    return () => {
      isMounted = false;
      clearInterval(statusInterval);
      clearInterval(tradingStatusInterval);
    };
  }, [mt5Connected, accountInfo, isTrading, fetchTradingStatus, fetchTradingData]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    const setupTarget = searchParams.get('setup');

    if (setupTarget === 'mt5' && !onboarding.mt5_connected && !hasLinkedMt5Account) {
      setShowLoginModal(true);
      setupPromptHandled.current = true;
      return;
    }

    if (setupTarget === 'mt5' && hasLinkedMt5Account) {
      setSearchParams({}, { replace: true });
      setupPromptHandled.current = true;
      return;
    }

    if (setupTarget === 'config' && onboarding.mt5_connected && !onboarding.config_set) {
      setShowConfigModal(true);
      setupPromptHandled.current = true;
      return;
    }

    if (!setupPromptHandled.current && !onboarding.mt5_connected && !hasLinkedMt5Account) {
      setShowLoginModal(true);
      setupPromptHandled.current = true;
      return;
    }

    if (!setupPromptHandled.current && onboarding.mt5_connected && !onboarding.config_set) {
      setShowConfigModal(true);
      setupPromptHandled.current = true;
    }
  }, [hasLinkedMt5Account, isLoading, onboarding, searchParams, setSearchParams]);
  
  // Fetch account info after MT5 login
  const fetchAccountInfo = async () => {
    try {
      const response = await api.get('/api/account').catch(() => null);
      if (response?.data) {
        setAccountInfo(response.data);
        setIsTrading(Boolean(response.data.bot_running));
      }
    } catch (error) {
      console.debug('Error fetching account info:', error.message);
    }
  };
  
  // Set up data refresh interval - only when trading is active and config is set
  useEffect(() => {
    let isMounted = true;
    
    // Define refreshData first
    const refreshData = () => {
      if (isMounted && document.visibilityState === 'visible') {
        fetchTradingData().catch(error => {
          console.debug('Background refresh failed:', error);
          // Silently fail for background refreshes
        });
      }
    };
    
    const cleanup = () => {
      isMounted = false;
      if (dataRefreshInterval.current) {
        clearInterval(dataRefreshInterval.current);
        dataRefreshInterval.current = null;
      }
      document.removeEventListener('visibilitychange', refreshData);
    };
    
    // Only start refreshing if:
    // 1. MT5 is connected
    // 2. Account info is loaded
    // 3. Trading is active (isTrading is true)
    const shouldRefresh = Boolean(accountInfo && (isTrading || mt5Connected || mt5Credentials.login));
    
    if (!shouldRefresh) {
      return cleanup;
    }
    
    // Initial data load
    refreshData();
    
    // Set up refresh interval (every 10 seconds for live data)
    dataRefreshInterval.current = setInterval(refreshData, 10000);
    
    // Set up visibility change listener
    document.addEventListener('visibilitychange', refreshData);
    
    return cleanup;
  }, [fetchTradingData, mt5Connected, accountInfo, isTrading, mt5Credentials.login]);
  
  // Handle start/stop trading
  const toggleTrading = async () => {
    if (!hasLinkedMt5Account) {
      toast.error('Connect your MT5 account before starting trading.');
      setShowLoginModal(true);
      return;
    }

    if (!onboarding.config_set) {
      toast.error('Save your trading configuration before starting trading.');
      setShowConfigModal(true);
      return;
    }

    try {
      setIsTradingActionPending(true);
      const wasTrading = isTrading;
      let response;

      if (wasTrading) {
          response = await api.post('/api/stop_trading', { close_positions: false });
      } else {
        response = await api.post('/api/start_trading');
      }
      
      if (response.data.success) {
        setIsTrading(!wasTrading);
        void fetchAccountInfo();
        void fetchTradingStatus();

        if (!wasTrading) {
          void fetchTradingData();
        }
        
        toast.success(
          wasTrading
            ? 'Trading bot stopped successfully'
            : 'Trading bot started successfully'
        );
      } else {
        if (!wasTrading && /already running/i.test(response.data.message || '')) {
          setIsTrading(true);
          await fetchTradingStatus();
          toast.info('Trading is already running.');
          return;
        }
        throw new Error(response.data.message || 'Failed to update trading status');
      }
    } catch (error) {
      console.error('Error toggling trading:', error);
      toast.error(`Failed to ${isTrading ? 'stop' : 'start'} trading: ${error.message}`);
    } finally {
      setIsTradingActionPending(false);
    }
  };
  
  // Handle MT5 login
  const handleMt5Login = async (credentials) => {
    try {
      setMt5Connected(true);
      setMt5Credentials(credentials);
      if (credentials.account_info) {
        setAccountInfo((previousAccountInfo) => ({
          ...previousAccountInfo,
          ...credentials.account_info,
          server: credentials.account_info.server || credentials.mt5_server,
          company: credentials.account_info.company || credentials.broker_name,
        }));
      }
      setShowLoginModal(false);
      const nextOnboarding = await refreshOnboarding();
      await fetchAccountInfo();
      await fetchTradingData();
      if (!nextOnboarding.config_set) {
        setSearchParams({ setup: 'config' }, { replace: true });
        setShowConfigModal(true);
      } else {
        setSearchParams({}, { replace: true });
      }
      return true;
    } catch (error) {
      console.error('MT5 login failed:', error);
      return false;
    }
  };

  const handleMt5Logout = async () => {
    try {
      setIsLoading(true);
      const response = await api.post('/api/mt5/logout');
      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Failed to disconnect MT5');
      }

      setMt5Connected(false);
      setMt5Credentials({
        login: '',
        password: '',
        server: '',
        broker_name: '',
        account_type: 'demo'
      });
      setAccountInfo(null);
      setActivePositions([]);
      setTradeHistory([]);
      setIsTrading(false);
      await refreshOnboarding();
      setSearchParams({ setup: 'mt5' }, { replace: true });
      toast.success('MT5 disconnected successfully');
    } catch (error) {
      console.error('MT5 logout failed:', error);
      toast.error(error.response?.data?.detail || error.message || 'Failed to disconnect MT5');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle trade close
  const handleCloseTrade = async (tradeId) => {
    setActivePositions((positions) => positions.filter((position) => position.ticket !== tradeId));
    fetchTradingData();
  };
  
  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Failed to logout. Please try again.');
    }
  };
  
  // Loading state
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#07111a] text-white">
        <div className="fixed inset-0 -z-10 bg-[radial-gradient(circle_at_top_left,_rgba(27,186,123,0.14),_transparent_30%),radial-gradient(circle_at_80%_20%,_rgba(45,122,255,0.12),_transparent_28%),linear-gradient(180deg,_#09131d_0%,_#05080c_100%)]" />
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-cyan-300"></div>
          <p className="mt-4 text-slate-300">Loading account...</p>
        </div>
      </div>
    );
  }

  const balanceValue = accountInfo?.balance ?? 0;
  const equityValue = accountInfo?.equity ?? accountInfo?.balance ?? 0;
  const marginValue = accountInfo?.margin ?? 0;
  const freeMarginValue = accountInfo?.free_margin ?? 0;
  const accountLabel = hasLinkedMt5Account
    ? `${mt5Credentials.account_type || 'demo'} account`
    : 'awaiting connection';
  const accountServer = accountInfo?.server || mt5Credentials.server || '--';
  const accountLeverage = accountInfo?.leverage ? `1:${accountInfo.leverage}` : '--';
  const accountCurrency = accountInfo?.currency || 'USD';
  const accountLogin = accountInfo?.login || mt5Credentials.login || '--';
  const accountCompany = accountInfo?.company || mt5Credentials.broker_name || '--';
  const botStartedAt = accountInfo?.bot_start_time
    ? new Date(accountInfo.bot_start_time).toLocaleString()
    : null;
  const sidebarActions = [
    {
      label: 'Account overview',
      description: 'View balance, equity, and MT5 account details.',
      onClick: () => window.scrollTo({ top: 0, behavior: 'smooth' }),
      tone: 'neutral',
    },
    {
      label: mt5Connected ? 'Reconnect MT5' : 'Connect MT5',
      description: 'Authenticate your MetaTrader 5 session.',
      onClick: () => setShowLoginModal(true),
      tone: 'primary',
    },
    {
      label: 'Bot configuration',
      description: 'Open symbol, risk, and limit settings.',
      onClick: () => setShowConfigModal(true),
      tone: 'neutral',
    },
    {
      label: isTrading ? 'Stop trading bot' : 'Start trading bot',
      description: 'Control automated execution state.',
      onClick: () => {
        if (!isTradingActionPending) {
          toggleTrading();
        }
      },
      tone: isTrading ? 'danger' : 'success',
      disabled: isTradingActionPending,
    },
  ];
  
  // Main render
  return (
    <div className="min-h-screen bg-[#f5f7fb] text-slate-900">
      <div className="mx-auto flex min-h-screen w-full max-w-[1680px]">
        <aside className="hidden w-[280px] border-r border-slate-200 bg-white/90 p-5 lg:block">
          <BrandLogo to="/account" size="compact" />
          <div className="mt-8 rounded-2xl border border-emerald-200/50 bg-emerald-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">MetaTrader 5 only</p>
            <p className="mt-2 text-sm text-emerald-900">
              This workspace is for MT5 login, bot configuration, and monitoring automated trading activity.
            </p>
          </div>
          <div className="mt-6 space-y-2">
            {sidebarActions.map((action) => (
              <button
                key={action.label}
                type="button"
                onClick={action.onClick}
                disabled={action.disabled}
                className={`w-full rounded-xl border px-3 py-3 text-left transition ${
                  action.tone === 'primary'
                    ? 'border-slate-900 bg-slate-900 text-white hover:bg-slate-800'
                    : action.tone === 'success'
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100'
                      : action.tone === 'danger'
                        ? 'border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100'
                        : 'border-slate-200 bg-white text-slate-800 hover:bg-slate-100'
                } ${action.disabled ? 'cursor-not-allowed opacity-60' : ''}`}
              >
                <div className="text-sm font-semibold">{action.label}</div>
                <div className={`mt-1 text-xs ${action.tone === 'primary' ? 'text-slate-200' : 'text-slate-500'}`}>
                  {action.description}
                </div>
              </button>
            ))}
          </div>
        </aside>

        <div className="min-w-0 flex-1">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1280px] items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-6">
            <div className="lg:hidden">
              <BrandLogo to="/account" size="compact" />
            </div>
            <nav className="hidden items-center gap-2 md:flex">
              <button
                onClick={() => navigate('/account')}
                className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
                type="button"
              >
                User Account
              </button>
              <button
                onClick={() => navigate('/')}
                className="rounded-xl px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-900"
                type="button"
              >
                Home
              </button>
              <button
                onClick={() => setShowConfigModal(true)}
                className="rounded-xl px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-900"
                type="button"
              >
                Configuration
              </button>
            </nav>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={fetchTradingData}
              disabled={isRefreshing}
              className={`inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white transition ${isRefreshing ? 'text-slate-400' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`}
              type="button"
            >
              <FiRefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>

            <div className="hidden text-right sm:block">
              <div className="text-sm font-semibold text-slate-900">{accountLogin !== '--' ? `Account ${accountLogin}` : 'User account'}</div>
              <div className="text-xs text-slate-500">{mt5Connected ? 'MT5 connected' : hasLinkedMt5Account ? 'MT5 account linked' : 'MT5 not connected'}</div>
            </div>

            <button
              onClick={handleLogout}
              className="hidden rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 hover:text-slate-900 sm:inline-flex"
              type="button"
            >
              Sign out
            </button>

            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-100 hover:text-slate-900 focus:outline-none"
                type="button"
              >
                <span className="sr-only">Open user menu</span>
                <FiUser className="h-4 w-4" />
              </button>

              {showUserMenu && (
                <div className="absolute right-0 mt-3 w-56 rounded-2xl border border-slate-200 bg-white p-2 shadow-[0_20px_50px_rgba(15,23,42,0.14)]">
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      setShowConfigModal(true);
                    }}
                    className="block w-full rounded-xl px-4 py-3 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-100"
                    type="button"
                  >
                    Open configuration
                  </button>
                  <button
                    onClick={handleLogout}
                    className="block w-full rounded-xl px-4 py-3 text-left text-sm font-medium text-rose-200 transition hover:bg-rose-300/10"
                    type="button"
                  >
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1280px] px-4 pb-10 pt-6 sm:px-6 lg:px-8">
        <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_10px_25px_rgba(15,23,42,0.06)] sm:p-6">
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">User account</h1>
          <p className="mt-2 text-sm text-slate-600 sm:text-base">
            Connect your MetaTrader 5 account, configure bot risk settings, and monitor live trading from your account workspace.
          </p>
        </section>

        <section className="mb-6 grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="rounded-[1.5rem] border border-slate-200 bg-white p-6 shadow-[0_12px_28px_rgba(15,23,42,0.08)] lg:p-8">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">Account overview</p>
            <div className="mt-5 grid gap-5 md:grid-cols-[1.2fr_0.8fr] md:items-end">
              <div>
                <div className="text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">{formatCurrency(balanceValue)}</div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-1">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Equity</div>
                  <div className="mt-2 text-xl font-semibold text-slate-900">{formatCurrency(equityValue)}</div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Account mode</div>
                  <div className="mt-2 text-xl font-semibold capitalize text-slate-900">{accountLabel}</div>
                </div>
              </div>
            </div>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Account login</div>
                <div className="mt-2 text-lg font-semibold text-slate-900">{accountLogin}</div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Server</div>
                <div className="mt-2 text-lg font-semibold text-slate-900">{accountServer}</div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Leverage</div>
                <div className="mt-2 text-lg font-semibold text-slate-900">{accountLeverage}</div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Currency</div>
                <div className="mt-2 text-lg font-semibold text-slate-900">{accountCurrency}</div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Margin</div>
                <div className="mt-2 text-lg font-semibold text-slate-900">{formatCurrency(marginValue, false, accountCurrency)}</div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Free margin</div>
                <div className="mt-2 text-lg font-semibold text-slate-900">{formatCurrency(freeMarginValue, false, accountCurrency)}</div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 xl:col-span-2">
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Broker</div>
                <div className="mt-2 text-lg font-semibold text-slate-900">{accountCompany}</div>
              </div>
            </div>
          </div>

          <div className="rounded-[1.5rem] border border-slate-200 bg-white p-6 shadow-[0_12px_28px_rgba(15,23,42,0.08)] lg:p-8">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-400">Trading status</p>
                <h2 className="mt-3 text-3xl font-bold text-slate-900">{mt5Connected ? 'MT5 connected' : 'Connect your MT5 account'}</h2>
                <p className="mt-3 text-sm text-slate-600">
                  {mt5Connected
                    ? `${mt5Credentials.account_type || 'demo'} account connected${accountServer !== '--' ? ` on ${accountServer}` : ''}.`
                    : hasLinkedMt5Account
                      ? `${mt5Credentials.account_type || 'demo'} account linked${accountServer !== '--' ? ` on ${accountServer}` : ''}. Connect again to refresh live session data.`
                      : 'Connect MT5 to load live account data.'}
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs uppercase tracking-[0.18em] text-slate-700">
                    Subscription: {subscriptionStatus || 'inactive'}
                  </span>
                  <span className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs uppercase tracking-[0.18em] text-slate-700">
                    Config: {onboarding.config_set ? 'saved' : 'required'}
                  </span>
                  <span className={`rounded-lg border px-3 py-2 text-xs uppercase tracking-[0.18em] ${isTrading ? 'border-emerald-300/20 bg-emerald-300/10 text-emerald-700' : 'border-slate-200 bg-slate-50 text-slate-700'}`}>
                    Bot: {isTrading ? 'running' : 'stopped'}
                  </span>
                </div>
                <div className={`mt-4 rounded-xl border px-4 py-3 text-sm ${isTrading ? 'border-emerald-300/20 bg-emerald-300/10 text-emerald-700' : 'border-slate-200 bg-slate-50 text-slate-700'}`}>
                  {isTrading
                    ? `Trading is running.${botStartedAt ? ` Started at ${botStartedAt}.` : ''}`
                    : 'Trading is currently stopped.'}
                </div>
              </div>
              <span className={`inline-flex rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] ${mt5Connected ? 'border-emerald-300/20 bg-emerald-300/10 text-emerald-200' : 'border-amber-300/20 bg-amber-300/10 text-amber-200'}`}>
                {mt5Connected ? 'Connected' : hasLinkedMt5Account ? 'Linked' : 'Waiting'}
              </span>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              {!mt5Connected ? (
                <button
                  onClick={() => setShowLoginModal(true)}
                  className="inline-flex items-center rounded-2xl bg-gradient-to-r from-emerald-300 to-cyan-300 px-5 py-3 text-sm font-bold text-slate-950 transition hover:scale-[1.02]"
                  type="button"
                >
                  {hasLinkedMt5Account ? 'Reconnect MT5' : 'Connect to MT5'}
                </button>
              ) : (
                <button
                  onClick={handleMt5Logout}
                  disabled={isLoading || isTrading}
                  className="inline-flex items-center rounded-2xl border border-amber-300/20 bg-amber-300/10 px-5 py-3 text-sm font-semibold text-amber-100 transition hover:bg-amber-300/15 disabled:cursor-not-allowed disabled:opacity-50"
                  type="button"
                >
                  Disconnect MT5
                </button>
              )}

              <button
                onClick={toggleTrading}
                disabled={isTradingActionPending}
                className={`inline-flex items-center rounded-2xl px-5 py-3 text-sm font-bold text-white transition disabled:cursor-not-allowed disabled:opacity-50 ${
                  isTrading ? 'bg-rose-500 hover:bg-rose-400' : 'bg-emerald-500 hover:bg-emerald-400'
                }`}
                type="button"
                title={isTradingActionPending ? 'Trading controls are updating' : ''}
              >
                {isTrading ? 'Stop Trading' : 'Start Trading'}
              </button>

                <button
                  onClick={() => setShowConfigModal(true)}
                className="inline-flex items-center rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
                  type="button"
                >
                Open configuration
              </button>
            </div>
            {!hasLinkedMt5Account && (
              <p className="mt-3 text-sm text-slate-400">
                Start and stop trading controls appear here. Connect MT5 to enable them.
              </p>
            )}
          </div>
        </section>

        <section className="mb-6 grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-[1rem] border border-white/10 bg-[#111922] p-5">
            <div className="flex items-center gap-4">
              <div className="rounded-2xl border border-cyan-300/20 bg-cyan-300/10 p-3 text-cyan-200"><FiDollar className="h-6 w-6" /></div>
              <div>
                <p className="text-sm text-slate-400">Balance</p>
                <div className="mt-1 text-2xl font-black text-white">{formatCurrency(balanceValue)}</div>
              </div>
            </div>
          </div>

          <div className="rounded-[1rem] border border-white/10 bg-[#111922] p-5">
            <div className="flex items-center gap-4">
              <div className="rounded-2xl border border-emerald-300/20 bg-emerald-300/10 p-3 text-emerald-200"><FiTrendingUp className="h-6 w-6" /></div>
              <div>
                <p className="text-sm text-slate-400">Daily P/L</p>
                <div className="mt-1 text-2xl font-black text-white">{formatCurrency(performanceMetrics.dailyProfit, true)}</div>
                <div className="mt-1 text-sm">{formatPercentage(performanceMetrics.dailyReturn || 0)}</div>
              </div>
            </div>
          </div>

          <div className="rounded-[1rem] border border-white/10 bg-[#111922] p-5">
            <div className="flex items-center gap-4">
              <div className="rounded-2xl border border-violet-300/20 bg-violet-300/10 p-3 text-violet-200"><FiActivity className="h-6 w-6" /></div>
              <div>
                <p className="text-sm text-slate-400">Active trades</p>
                <div className="mt-1 text-2xl font-black text-white">{activePositions.length}</div>
              </div>
            </div>
          </div>

          <div className="rounded-[1rem] border border-white/10 bg-[#111922] p-5">
            <div className="flex items-center gap-4">
              <div className="rounded-2xl border border-amber-300/20 bg-amber-300/10 p-3 text-amber-200"><FiPieChart className="h-6 w-6" /></div>
              <div>
                <p className="text-sm text-slate-400">Win rate</p>
                <div className="mt-1 text-2xl font-black text-white">{performanceMetrics.winRate ? `${performanceMetrics.winRate.toFixed(1)}%` : '--'}</div>
              </div>
            </div>
          </div>
        </section>

        <section className="mb-6 rounded-[1.5rem] border border-white/10 bg-[#111922] p-6 shadow-[0_18px_50px_rgba(0,0,0,0.24)] lg:p-8">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-400">Performance</p>
              <h3 className="mt-2 text-2xl font-bold text-white">Balance history</h3>
            </div>
            <div className="text-sm text-slate-400">Updates while trading is running</div>
          </div>
          <div className="h-64">
            <Line
              data={priceData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                  tooltip: { mode: 'index', intersect: false },
                },
                scales: {
                  x: { 
                    grid: { display: false },
                    ticks: { color: 'rgba(148, 163, 184, 0.65)' }
                  },
                  y: { 
                    grid: { color: 'rgba(255, 255, 255, 0.08)' },
                    ticks: { color: 'rgba(148, 163, 184, 0.75)' }
                  }
                },
                elements: {
                  point: { radius: 0 },
                  line: { tension: 0.4 }
                },
                interaction: { mode: 'nearest', axis: 'x', intersect: false },
                animation: { duration: 0 }
              }}
            />
          </div>
        </section>

        <section className="mb-6 overflow-hidden rounded-[1.5rem] border border-white/10 bg-[#111922] shadow-[0_18px_50px_rgba(0,0,0,0.24)]">
          <div className="flex items-center justify-between border-b border-white/10 px-6 py-5">
            <h3 className="text-lg font-semibold text-white">Active positions</h3>
            <span className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-300">
                {activePositions.length} Open
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-white/10">
              <thead className="bg-slate-950/35">
                <tr>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Symbol</th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Volume</th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Price</th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">SL</th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">TP</th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Profit</th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Time</th>
                  <th scope="col" className="relative px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 bg-transparent">
                {activePositions.length > 0 ? (
                  activePositions.map((position) => (
                    <TradeRow 
                      key={position.ticket} 
                      trade={position} 
                      onCloseTrade={handleCloseTrade} 
                    />
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="px-6 py-6 text-center text-sm text-slate-400">
                      No active positions
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="overflow-hidden rounded-[1.5rem] border border-white/10 bg-[#111922] shadow-[0_18px_50px_rgba(0,0,0,0.24)]">
          <div className="flex items-center justify-between border-b border-white/10 px-6 py-5">
            <h3 className="text-lg font-semibold text-white">Recent trades</h3>
            <span className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-300">
                Last 10 Trades
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-white/10">
              <thead className="bg-slate-950/35">
                <tr>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Symbol</th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Type</th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Volume</th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Open Price</th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Close Price</th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Profit</th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 bg-transparent">
                {tradeHistory.slice(0, 10).map((trade) => {
                  const isBuy = trade.type === 'buy' || trade.type === 0;
                  const profit = parseFloat(trade.profit || 0);
                  const isPositive = profit > 0;
                  const isNegative = profit < 0;
                  
                  return (
                    <tr key={trade.ticket} className="transition hover:bg-white/[0.03]">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-200">
                        {trade.symbol}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold uppercase tracking-[0.16em] ${
                          isBuy ? 'border-emerald-300/20 bg-emerald-300/10 text-emerald-200' : 'border-rose-300/20 bg-rose-300/10 text-rose-200'
                        }`}>
                          {isBuy ? 'Buy' : 'Sell'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                        {trade.volume}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                        {trade.openPrice?.toFixed(5) || '--'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                        {trade.closePrice?.toFixed(5) || '--'}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${
                        isPositive ? 'text-emerald-300' : isNegative ? 'text-rose-300' : 'text-slate-300'
                      }`}>
                        {formatCurrency(profit, true)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                        {new Date(trade.closeTime || trade.openTime).toLocaleString()}
                      </td>
                    </tr>
                  );
                })}
                {tradeHistory.length === 0 && (
                  <tr>
                    <td colSpan="7" className="px-6 py-6 text-center text-sm text-slate-400">
                      No trade history available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

      </main>
        </div>
      </div>
      
      <div
        className={`fixed inset-0 z-50 overflow-y-auto bg-black/75 p-3 transition-opacity duration-300 sm:p-6 ${showLoginModal ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
        onClick={() => setShowLoginModal(false)}
      >
        <div className="flex min-h-full items-start justify-center py-4 sm:items-center sm:py-8">
        <div
          className="w-full max-w-xl"
          onClick={(event) => event.stopPropagation()}
        >
          <Mt5Login
            isOpen={showLoginModal}
            onClose={() => setShowLoginModal(false)}
            onSuccess={handleMt5Login}
            initialCredentials={mt5Credentials}
          />
        </div>
        </div>
      </div>
      
      {showConfigModal && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto bg-black/60 p-3 sm:p-6"
          onClick={() => setShowConfigModal(false)}
        >
          <div className="flex min-h-full items-start justify-center py-4 sm:items-center sm:py-8">
          <div
            className="max-h-[calc(100vh-2rem)] w-full max-w-2xl overflow-y-auto rounded-[2rem] border border-white/10 bg-[#0d1722] p-5 shadow-[0_30px_80px_rgba(0,0,0,0.45)] sm:max-h-[calc(100vh-4rem)] sm:p-6"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="sticky top-0 z-10 mb-4 flex items-center justify-between border-b border-white/10 bg-[#0d1722] pb-4">
              <h3 className="text-xl font-semibold text-white">Trading configuration</h3>
              <button 
                onClick={() => setShowConfigModal(false)}
                className="text-slate-400 transition hover:text-white"
                type="button"
              >
                <FiX className="h-6 w-6" />
              </button>
            </div>
            <ConfigInput onSuccess={() => {
              setShowConfigModal(false);
              setSearchParams({}, { replace: true });
              refreshOnboarding();
              fetchTradingData(); // Refresh data after saving config
            }} />
          </div>
          </div>
        </div>
      )}
      
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
    </div>
  );
};

export default Dashboard;
